import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { DatePipe } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ListAbstract } from 'app/views/pages/abstract/list-abstract';
import { OdataEntityField } from 'services/models/odata/odataEntityField';
import { EmployeeFeedbackService } from 'services/feedback/employeeFeedback.service';
import { EmployeeFeedbackModel } from 'services/models/employeeFeedback.model';
import { EmployeeModel } from 'services/models/employee.model';
import { FilterType } from 'services/models/odata/filter';
import { EmployeeManagementService } from 'services/corporate/employeeManagement.service';
import f from 'odata-filter-builder';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FeedbackFormComponent, FeedbackFormMode } from '../feedback-form/feedback-form.component';

const timeSearch = 1000;

const defaultCurrentSort: { prop: string; dir: 'asc' | 'desc' } = {
  prop: 'creationDate',
  dir: 'asc',
};

const defaultFilters = {
  searchQuery: '',
};
@Component({
  selector: 'app-received-feedback-list',
  templateUrl: './received-feedback-list.component.html',
  styleUrls: ['./received-feedback-list.component.scss'],
})
export class ReceivedFeedbackListComponent
  extends ListAbstract<EmployeeFeedbackModel | EmployeeModel, EmployeeFeedbackService>
  implements OnInit, OnDestroy, OnChanges {

  @Input() organicFeedback: boolean = false;
  @Input() employeeId: string;

  public hasHierarchy: boolean = true;
  public currentSort: { prop: string; dir: 'asc' | 'desc' } = { prop: 'creationDate', dir: 'asc' };
  newFeedbackEmployee?: EmployeeModel;
  title = 'Feedbacks';
  emitQueryMethod = true;
  isLoading: boolean = false;
  columns: any[] = [];

  private subscriptions = new Subscription();

  fields = [
    { field: "employeeManagerName", alias: "Remetente" },
    { field: "creationDate", alias: "Data", pipe: DatePipe, args: "dd/MM/yyyy HH:mm", filterType: FilterType.DATE }
  ].map((x) => x as OdataEntityField);

  constructor(
    public service: EmployeeFeedbackService,
    private datePipe: DatePipe,
    private ngbModal: NgbModal
  ) {
    super(service);
    this.canEdit = false;
    this.queryParams.top = 10;
    this.queryParams.select = [];
    this.queryParams.orderBy = 'creationDate';
    this.queryParams.orderByOrder = 'asc';
  }

  ngOnInit(): void {
   this.columns = [
  { name: 'Gestor', prop: 'employeeManagerName' },
  { name: 'Cargo', prop: 'employeeManagerJobTitle' },  { name: 'Data', prop: 'creationDate' },
  { name: 'Ação', action: 'view' }
];

    if (this.employeeId) {
      this.loadFeedbacks();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.employeeId) {
      const id = changes.employeeId.currentValue;
      if (id) {
        this.loadFeedbacks();
      } else {
        this.list = [];
        this.count = 0;
      }
    }
  }

resolveValue(row: any, prop?: string): string {
  if (!prop) return '—';
  if (prop === 'employeeManagerName') return row?.employeeManagerName || row?.employee?.name || row?.name || '—';
  if (prop === 'creationDate') return row?.creationDate ? this.datePipe.transform(row.creationDate, 'dd/MM/yyyy')! : '—';
  if (prop === 'employeeManagerJobTitle') return row?.employeeManagerJobTitle || row?.employee?.jobTitleName || row?.employeeJobTitle || '—';
  return row?.[prop] || '—';
}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public onSort(event: any) {
    if (event && event.sorts && event.sorts.length > 0) {
      const [sort] = event.sorts;
      const dir = sort.dir === 'desc' ? 'desc' : 'asc';
      this.currentSort = { prop: sort.prop, dir };
      this.queryParams.orderBy = sort.prop;
      this.queryParams.orderByOrder = dir;
      this.loadFeedbacks();
    }
  }

  public onPage(event: any) {
    this.queryParams.skip = event.offset * event.limit;
    this.queryParams.top = event.limit;
    this.loadFeedbacks();
  }

  public handleViewFeedback(obj: EmployeeFeedbackModel): void {
    const modalRef = this.ngbModal.open(FeedbackFormComponent, {
      animation: false,
      windowClass: 'feedback-form-drawer-modal',
      backdrop: 'static',
      keyboard: true,
      size: 'xl',
    });
    modalRef.componentInstance.mode = FeedbackFormMode.View;
    modalRef.componentInstance.feedbackView = obj;
    modalRef.result.then(
      (result: boolean) => { if (result) this.loadFeedbacks(); },
      (reason: boolean) => { if (reason) this.loadFeedbacks(); }
    );
  }

  public loadFeedbacks() {
    this.isLoading = true;
    const emptyFeedback = new EmployeeFeedbackModel();
    const dynamicFields = Object.keys(emptyFeedback);
    const selectableFields = Array.from(new Set([...dynamicFields, 'id', 'creationDate']));

    const baseFields = new Set(['id', 'creationDate', 'showToEmployee']);
    const uiFields = new Set(this.fields.map((f) => f.field));
    const columnFields = new Set((this.columns || []).map((c: any) => c.prop).filter(Boolean));
    const isSelectableField  = (name: string) => /change|start|keep|stop|competence|employee|manager|show|title|id/i.test(name);

    const paramsWithSelect = {
      ...this.queryParams,
      select: selectableFields.filter((f) => baseFields.has(f) || uiFields.has(f) || columnFields.has(f) || isSelectableField (f)),
    };

    const attemptList = (params: any, retried = false) => {
      this.service.listByEmployee(this.employeeId, params).subscribe({
        next: (response) => {
          let feedbacks = (response?.data || []).map((item: any) => ({ ...item }));
          if (!this.organicFeedback) {
            feedbacks = feedbacks.filter(f => f.showToEmployee === true);
          }
          this.list = feedbacks;
          this.count = feedbacks.length;
          this.isLoading = false;
        },
        error: (err) => {
          const message = err?.error?.exceptionMessage || err?.message || '';
          const requestUrl = err?.url || err?.error?.url || (err?.request && err.request.url) || '';

          if (retried) {
            this.list = [];
            this.count = 0;
            this.isLoading = false;
            return;
          }

          const m = /Could not find a property named '([^']+)'/.exec(message || '');
          const badProp = m?.[1];
        
          if (badProp || (err?.status === 400 && String(requestUrl).includes('$select'))) {
            const newParams: any = { ...params };
            if (badProp && Array.isArray(newParams.select)) {
              newParams.select = newParams.select.filter((s: string) => s !== badProp);
              if (newParams.select.length === 0) delete newParams.select;
            } else {
              delete newParams.select;
            }
            attemptList(newParams, true);
            return;
          }

          this.list = [];
          this.count = 0;
          this.isLoading = false;
        },
      });
    };

    attemptList(paramsWithSelect);
  }
}
